import React, { useCallback, useEffect, useState } from "react";
import { Row, Col } from "react-flexbox-grid";
import Paper from "@material-ui/core/Paper";
import TitleBar from "../Layout/titleBar";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Switch from "@material-ui/core/Switch";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import InputAdornment from "@material-ui/core/InputAdornment";
import { withStyles } from "@material-ui/core/styles";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import AutorenewOutlinedIcon from "@material-ui/icons/AutorenewOutlined";
import MonetizationOnOutlinedIcon from "@material-ui/icons/MonetizationOnOutlined";
import ClearIcon from "@material-ui/icons/Clear";
import _axios from "../../apiClient";
import * as Notifications from "../Common/notification";

const CuentaCorrienteSwitch = withStyles({
  switchBase: {
    color: "#B0BEC5",
    "&$checked": {
      color: "#2E7D32",
      "& + $bar": {
        backgroundColor: "#2E7D32",
        opacity: 0.35,
      },
    },
  },
  checked: {},
  bar: {},
})(Switch);

const CuentaCorriente = ({ instalacion }) => {
  const poppinsFont = '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif';
  const cuentaCorrienteSinLimiteEnabled = instalacion?.cuentaCorrienteSinLimiteEnabled === true;
  const [clientes, setClientes] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(0);
  const [totalObjects, setTotalObjects] = useState(0);
  const [viewMode, setViewMode] = useState("list");
  const [clienteDetalle, setClienteDetalle] = useState(null);
  const [movimientosDetalle, setMovimientosDetalle] = useState([]);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [dialogState, setDialogState] = useState({
    open: false,
    type: null,
    cliente: null,
    limite: "",
    saldoDisponible: "",
    montoPago: "",
    motivo: "",
  });

  const thStyle = {
    textAlign: "left",
    padding: "10px 8px",
    borderBottom: "1px solid #E5E7EB",
    whiteSpace: "nowrap",
  };

  const tdStyle = {
    padding: "8px",
    borderBottom: "1px solid #F3F4F6",
    verticalAlign: "middle",
  };

  const normalizeCuentaCorriente = (response) => ({
    habilitada: response?.cuentaCorrienteHabilitada ?? response?.CuentaCorrienteHabilitada ?? false,
    limite: Number(response?.cuentaCorrienteLimite ?? response?.CuentaCorrienteLimite ?? 0),
    saldoDisponible: Number(
      response?.cuentaCorrienteSaldoDisponible ?? response?.CuentaCorrienteSaldoDisponible ?? 0
    ),
    fechaActualizacion:
      response?.cuentaCorrienteFechaActualizacion ??
      response?.CuentaCorrienteFechaActualizacion ??
      null,
  });

  const formatCurrency = (value) => {
    const numericValue = Number(value || 0);
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericValue);
  };

  const formatMotivoWithCurrency = (motivo) => {
    if (!motivo || typeof motivo !== "string") {
      return motivo || "-";
    }

    const match = motivo.match(/^(.* - )(-?[0-9.,]+)$/);
    if (!match) {
      return motivo;
    }

    const prefix = match[1];
    const rawAmount = match[2].trim();
    const normalized = rawAmount.replace(/\./g, "").replace(/,/g, ".");
    const amount = Number(normalized);

    if (!Number.isFinite(amount)) {
      return motivo;
    }

    return `${prefix}${formatCurrency(amount)}`;
  };

  const parseFormattedNumberInput = (input) => {
    const rawValue = String(input || "").replace(/\s/g, "");
    if (!rawValue) {
      return "";
    }

    const cleaned = rawValue.replace(/[^0-9.,]/g, "");
    const lastComma = cleaned.lastIndexOf(",");
    const lastDot = cleaned.lastIndexOf(".");
    const decimalSeparatorIndex = Math.max(lastComma, lastDot);

    if (decimalSeparatorIndex === -1) {
      return cleaned.replace(/[.,]/g, "");
    }

    const integerPart = cleaned.slice(0, decimalSeparatorIndex).replace(/[.,]/g, "");
    const decimalPart = cleaned.slice(decimalSeparatorIndex + 1).replace(/[.,]/g, "");

    if (!decimalPart.length) {
      return `${integerPart}.`;
    }

    return `${integerPart}.${decimalPart}`;
  };

  const formatNumberForInput = (value) => {
    if (value === null || value === undefined || value === "") {
      return "";
    }

    const normalizedValue = String(value);
    const hasTrailingDot = normalizedValue.endsWith(".");
    const [integerPartRaw, decimalPartRaw = ""] = normalizedValue.split(".");
    const integerDigits = integerPartRaw.replace(/\D/g, "");
    const integerNumber = integerDigits ? Number(integerDigits) : 0;
    const formattedInteger = new Intl.NumberFormat("es-AR", {
      maximumFractionDigits: 0,
    }).format(integerNumber);

    if (hasTrailingDot) {
      return `${formattedInteger},`;
    }

    if (decimalPartRaw !== "") {
      return `${formattedInteger},${decimalPartRaw}`;
    }

    return formattedInteger;
  };

  const loadClientes = useCallback(async (requestedPage, requestedPageSize, requestedFiltro) => {
    try {
      setLoading(true);

      const response = await _axios.get(`/api/Usuario/GetCuentaCorrienteClientes`, {
        params: {
          filtro: requestedFiltro || null,
          page: requestedPage,
          pageSize: requestedPageSize,
        },
      });

      const payload = response?.data || {};
      const raw = Array.isArray(payload?.result) ? payload.result : [];
      const mapped = raw.map((x) => {
        const nombre = x?.nombre ?? x?.Nombre ?? "";
        const apellido = x?.apellido ?? x?.Apellido ?? "";
        const rawIdCliente = x?.idCliente ?? x?.IdCliente;
        const parsedIdCliente = Number(rawIdCliente || 0);
        const hasCliente = Number.isFinite(parsedIdCliente) && parsedIdCliente > 0;
        const idUsuario = x?.idUsuario ?? x?.IdUsuario;
        const rowId = `${idUsuario || "sin-usuario"}-${hasCliente ? parsedIdCliente : "sin-cliente"}`;

        return {
          rowId,
          idUsuario,
          idCliente: hasCliente ? parsedIdCliente : 0,
          hasCliente,
          nombre:
            `${apellido} ${nombre}`.trim() || x?.nombreUsuario || x?.NombreUsuario || "Sin nombre",
          email: x?.email ?? x?.Email ?? "",
          habilitada: x?.cuentaCorrienteHabilitada ?? x?.CuentaCorrienteHabilitada ?? false,
          limite: Number(x?.cuentaCorrienteLimite ?? x?.CuentaCorrienteLimite ?? 0),
          saldoDisponible: Number(
            x?.cuentaCorrienteSaldoDisponible ?? x?.CuentaCorrienteSaldoDisponible ?? 0
          ),
          fechaActualizacion:
            x?.cuentaCorrienteFechaActualizacion ?? x?.CuentaCorrienteFechaActualizacion ?? null,
        };
      });

      setClientes(mapped);
      setTotalPages(Number(payload?.totalPages ?? payload?.TotalPages ?? 0));
      setTotalObjects(Number(payload?.totalObjects ?? payload?.TotalObjects ?? 0));
    } catch (error) {
      Notifications.showError(
        error?.response?.data?.error || error?.response?.data || "Error al cargar clientes."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClientes(page, pageSize, filtro);
  }, [filtro, loadClientes, page, pageSize]);

  const updateClienteRow = (rowId, patch) => {
    setClientes((prev) => prev.map((c) => (c.rowId === rowId ? { ...c, ...patch } : c)));
  };

  const limpiarFiltro = () => {
    if (!filtro) {
      return;
    }

    setFiltro("");

    if (page !== 0) {
      setPage(0);
      return;
    }

    loadClientes(0, pageSize, "");
  };

  const validarClienteAsociado = (cliente) => {
    if (cliente.hasCliente) {
      return true;
    }

    Notifications.showError(
      "Este usuario no tiene un cliente asociado. No se puede operar Cuenta Corriente."
    );
    return false;
  };

  const actualizarHabilitada = async (cliente, habilitada) => {
    if (!validarClienteAsociado(cliente)) {
      return;
    }

    try {
      const response = await _axios.post(`/api/Usuario/SetCuentaCorriente`, {
        idCliente: cliente.idCliente,
        habilitada,
        limite: Number(cliente.limite || 0),
        motivo: "Actualización de habilitación desde módulo Cuenta Corriente",
      });

      updateClienteRow(cliente.rowId, {
        ...normalizeCuentaCorriente(response.data),
      });

      Notifications.showSuccess(`Cuenta Corriente actualizada para ${cliente.nombre}.`);
    } catch (error) {
      Notifications.showError(
        error?.response?.data?.error || error?.response?.data || "Error al actualizar cliente."
      );

      updateClienteRow(cliente.rowId, {
        habilitada: !habilitada,
      });
    }
  };

  const abrirDialogo = (type, cliente) => {
    if (!validarClienteAsociado(cliente)) {
      return;
    }

    setDialogState({
      open: true,
      type,
      cliente,
      limite: String(cliente.limite || 0),
      saldoDisponible: String(cliente.saldoDisponible || 0),
      montoPago: "",
      motivo: "",
    });
  };

  const cerrarDialogo = () => {
    setDialogState((prev) => ({ ...prev, open: false }));
  };

  const updateDialogField = (field, value) => {
    setDialogState((prev) => ({ ...prev, [field]: value }));
  };

  const confirmarDialogo = async () => {
    const { type, cliente, limite, saldoDisponible, montoPago, motivo } = dialogState;

    if (!cliente) {
      return;
    }

    if (!motivo.trim()) {
      Notifications.showError("El motivo es obligatorio.");
      return;
    }

    try {
      if (type === "edit") {
        const limiteNum = Number(limite);
        const saldoNum = Number(saldoDisponible);

        if (!Number.isFinite(limiteNum) || limiteNum < 0) {
          Notifications.showError("Ingresá un límite válido (mayor o igual a cero).");
          return;
        }

        if (!Number.isFinite(saldoNum) || saldoNum < 0) {
          Notifications.showError("Ingresá un saldo válido (mayor o igual a cero).");
          return;
        }

        if (saldoNum > limiteNum) {
          Notifications.showError("El saldo no puede ser mayor al límite.");
          return;
        }

        const response = await _axios.post(`/api/Usuario/EditarCuentaCorrienteConfiguracion`, {
          idCliente: cliente.idCliente,
          limite: limiteNum,
          saldoDisponible: saldoNum,
          motivo: motivo.trim(),
        });

        updateClienteRow(cliente.rowId, {
          ...normalizeCuentaCorriente(response.data),
        });

        Notifications.showSuccess(`Configuración actualizada para ${cliente.nombre}.`);
      }

      if (type === "reset") {
        const response = await _axios.post(`/api/Usuario/ResetCuentaCorrienteSaldo`, {
          idCliente: cliente.idCliente,
          motivo: motivo.trim(),
        });

        updateClienteRow(cliente.rowId, {
          ...normalizeCuentaCorriente(response.data),
        });

        Notifications.showSuccess(`Saldo reiniciado para ${cliente.nombre}.`);
      }

      if (type === "pago") {
        const montoNum = Number(montoPago);
        if (!Number.isFinite(montoNum) || montoNum <= 0) {
          Notifications.showError("Ingresá un monto válido mayor a cero.");
          return;
        }

        const response = await _axios.post(`/api/Usuario/RegistrarPagoCuentaCorriente`, {
          idCliente: cliente.idCliente,
          montoPago: montoNum,
          motivo: motivo.trim(),
        });

        updateClienteRow(cliente.rowId, {
          ...normalizeCuentaCorriente(response.data),
        });

        Notifications.showSuccess(`Pago registrado para ${cliente.nombre}.`);
      }

      cerrarDialogo();
    } catch (error) {
      Notifications.showError(
        error?.response?.data?.error || error?.response?.data || "Error al ejecutar la acción."
      );
    }
  };

  const editarConfiguracionCliente = (cliente) => abrirDialogo("edit", cliente);
  const resetSaldoCliente = (cliente) => abrirDialogo("reset", cliente);
  const registrarPagoCliente = (cliente) => abrirDialogo("pago", cliente);

  const verDetalleMovimientos = async (cliente) => {
    if (!validarClienteAsociado(cliente)) {
      return;
    }

    try {
      setLoadingDetalle(true);
      setClienteDetalle(cliente);
      setViewMode("detail");

      const response = await _axios.get(`/api/Usuario/GetCuentaCorrienteMovimientos`, {
        params: { idCliente: cliente.idCliente },
      });

      const movimientos = Array.isArray(response.data) ? response.data : [];
      movimientos.sort((a, b) => new Date(b.fechaRegistro) - new Date(a.fechaRegistro));
      setMovimientosDetalle(movimientos);
    } catch (error) {
      Notifications.showError(
        error?.response?.data?.error ||
          error?.response?.data ||
          "Error al cargar detalle de movimientos."
      );
      setViewMode("list");
      setClienteDetalle(null);
    } finally {
      setLoadingDetalle(false);
    }
  };

  return (
    <div style={{ fontFamily: poppinsFont }}>
      <TitleBar title="Cuenta Corriente" showSearch={false} />

      {instalacion && instalacion.cuentaCorrienteEnabled !== true ? (
        <Paper className="myBox" style={{ margin: "0px", padding: "20px" }}>
          La funcionalidad de Cuenta Corriente está deshabilitada para esta instalación.
        </Paper>
      ) : viewMode === "detail" ? (
        <Row>
          <Col md={12}>
            <Paper className="myBox" style={{ margin: "0px", padding: "16px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "12px",
                }}
              >
                <div style={{ fontWeight: 600 }}>
                  Detalle de movimientos - {clienteDetalle?.nombre || "Cliente"}
                </div>
                <Button
                  className="btnSecundarioFlat"
                  onClick={() => {
                    setViewMode("list");
                    setClienteDetalle(null);
                    setMovimientosDetalle([]);
                  }}
                >
                  VOLVER A LISTA
                </Button>
              </div>

              {loadingDetalle ? (
                <div style={{ padding: "12px", color: "#6B7280" }}>Cargando movimientos...</div>
              ) : movimientosDetalle.length === 0 ? (
                <div style={{ padding: "12px", color: "#6B7280" }}>
                  No hay movimientos para este cliente.
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                    <thead>
                      <tr>
                        <th
                          style={{
                            textAlign: "left",
                            padding: "10px",
                            borderBottom: "1px solid #E5E7EB",
                          }}
                        >
                          Fecha
                        </th>
                        <th
                          style={{
                            textAlign: "left",
                            padding: "10px",
                            borderBottom: "1px solid #E5E7EB",
                          }}
                        >
                          Tipo
                        </th>
                        <th
                          style={{
                            textAlign: "left",
                            padding: "10px",
                            borderBottom: "1px solid #E5E7EB",
                          }}
                        >
                          Pedido
                        </th>
                        <th
                          style={{
                            textAlign: "right",
                            padding: "10px",
                            borderBottom: "1px solid #E5E7EB",
                          }}
                        >
                          Monto
                        </th>
                        <th
                          style={{
                            textAlign: "right",
                            padding: "10px",
                            borderBottom: "1px solid #E5E7EB",
                          }}
                        >
                          Saldo anterior
                        </th>
                        <th
                          style={{
                            textAlign: "right",
                            padding: "10px",
                            borderBottom: "1px solid #E5E7EB",
                          }}
                        >
                          Saldo nuevo
                        </th>
                        <th
                          style={{
                            textAlign: "left",
                            padding: "10px",
                            borderBottom: "1px solid #E5E7EB",
                          }}
                        >
                          Motivo
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {movimientosDetalle.map((mov) => (
                        <tr key={mov.idClienteCuentaCorrienteMovimiento}>
                          <td style={{ padding: "10px", borderBottom: "1px solid #F3F4F6" }}>
                            {mov.fechaRegistro
                              ? new Date(mov.fechaRegistro).toLocaleString("es-AR")
                              : "-"}
                          </td>
                          <td style={{ padding: "10px", borderBottom: "1px solid #F3F4F6" }}>
                            {mov.tipoMovimiento || "-"}
                          </td>
                          <td style={{ padding: "10px", borderBottom: "1px solid #F3F4F6" }}>
                            {mov.idPedido || "-"}
                          </td>
                          <td
                            style={{
                              padding: "10px",
                              borderBottom: "1px solid #F3F4F6",
                              textAlign: "right",
                            }}
                          >
                            {formatCurrency(mov.monto)}
                          </td>
                          <td
                            style={{
                              padding: "10px",
                              borderBottom: "1px solid #F3F4F6",
                              textAlign: "right",
                            }}
                          >
                            {formatCurrency(mov.saldoAnterior)}
                          </td>
                          <td
                            style={{
                              padding: "10px",
                              borderBottom: "1px solid #F3F4F6",
                              textAlign: "right",
                            }}
                          >
                            {formatCurrency(mov.saldoNuevo)}
                          </td>
                          <td style={{ padding: "10px", borderBottom: "1px solid #F3F4F6" }}>
                            {formatMotivoWithCurrency(mov.motivo)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Paper>
          </Col>
        </Row>
      ) : (
        <Row>
          <Col md={12}>
            <Paper className="myBox" style={{ margin: "0px", padding: "16px" }}>
              <div style={{ fontWeight: 600, marginBottom: "10px" }}>Listado de clientes</div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: "12px" }}>
                <TextField
                  fullWidth
                  label="Buscar por nombre o email"
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                  InputProps={{
                    endAdornment: filtro ? (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="Limpiar filtro"
                          size="small"
                          onClick={limpiarFiltro}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
                <Button
                  className="btnSecundarioFlat"
                  onClick={() => {
                    if (page !== 0) {
                      setPage(0);
                    } else {
                      loadClientes(0, pageSize, filtro);
                    }
                  }}
                >
                  BUSCAR
                </Button>
              </div>

              <div style={{ marginTop: "16px", overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "13px",
                    tableLayout: "fixed",
                  }}
                >
                  <colgroup>
                    <col style={{ width: cuentaCorrienteSinLimiteEnabled ? "31%" : "25%" }} />
                    <col style={{ width: cuentaCorrienteSinLimiteEnabled ? "36%" : "28%" }} />
                    <col style={{ width: cuentaCorrienteSinLimiteEnabled ? "12%" : "10%" }} />
                    {!cuentaCorrienteSinLimiteEnabled && <col style={{ width: "10%" }} />}
                    {!cuentaCorrienteSinLimiteEnabled && <col style={{ width: "10%" }} />}
                    <col style={{ width: cuentaCorrienteSinLimiteEnabled ? "21%" : "17%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={thStyle}>Cliente</th>
                      <th style={thStyle}>Email</th>
                      <th style={{ ...thStyle, textAlign: "center" }}>Habilitada</th>
                      {!cuentaCorrienteSinLimiteEnabled && (
                        <th style={{ ...thStyle, textAlign: "right" }}>Límite</th>
                      )}
                      {!cuentaCorrienteSinLimiteEnabled && (
                        <th style={{ ...thStyle, textAlign: "right" }}>Saldo</th>
                      )}
                      <th style={thStyle}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr>
                        <td
                          colSpan={cuentaCorrienteSinLimiteEnabled ? 4 : 6}
                          style={{ padding: "12px", color: "#6B7280" }}
                        >
                          Cargando clientes...
                        </td>
                      </tr>
                    )}

                    {!loading && clientes.length === 0 && (
                      <tr>
                        <td
                          colSpan={cuentaCorrienteSinLimiteEnabled ? 4 : 6}
                          style={{ padding: "12px", color: "#6B7280" }}
                        >
                          No hay clientes para mostrar.
                        </td>
                      </tr>
                    )}

                    {!loading &&
                      clientes.map((cliente) => (
                        <tr key={cliente.rowId}>
                          <td style={tdStyle}>{cliente.nombre}</td>
                          <td
                            style={{
                              ...tdStyle,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={cliente.email || "-"}
                          >
                            {cliente.email || "-"}
                          </td>
                          <td
                            style={{
                              ...tdStyle,
                              textAlign: "center",
                            }}
                          >
                            <CuentaCorrienteSwitch
                              checked={!!cliente.habilitada}
                              onChange={(e) => {
                                updateClienteRow(cliente.rowId, {
                                  habilitada: e.target.checked,
                                });
                                actualizarHabilitada(cliente, e.target.checked);
                              }}
                              disabled={!cliente.hasCliente}
                            />
                          </td>
                          {!cuentaCorrienteSinLimiteEnabled && (
                            <td
                              style={{
                                ...tdStyle,
                                textAlign: "right",
                              }}
                            >
                              {formatCurrency(cliente.limite)}
                            </td>
                          )}
                          {!cuentaCorrienteSinLimiteEnabled && (
                            <td
                              style={{
                                ...tdStyle,
                                textAlign: "right",
                              }}
                            >
                              {formatCurrency(cliente.saldoDisponible)}
                            </td>
                          )}
                          <td style={tdStyle}>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: "6px",
                              }}
                            >
                              <Tooltip title="Ver detalle" placement="top">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => verDetalleMovimientos(cliente)}
                                    disabled={!cliente.hasCliente}
                                  >
                                    <VisibilityOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              {!cuentaCorrienteSinLimiteEnabled && (
                                <Tooltip title="Editar límite y saldo" placement="top">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => editarConfiguracionCliente(cliente)}
                                      disabled={!cliente.hasCliente || !cliente.habilitada}
                                    >
                                      <EditOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                              {!cuentaCorrienteSinLimiteEnabled && (
                                <Tooltip title="Reset saldo total" placement="top">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => resetSaldoCliente(cliente)}
                                      disabled={!cliente.hasCliente || !cliente.habilitada}
                                    >
                                      <AutorenewOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                              {!cuentaCorrienteSinLimiteEnabled && (
                                <Tooltip title="Registrar pago parcial" placement="top">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => registrarPagoCliente(cliente)}
                                      disabled={!cliente.hasCliente || !cliente.habilitada}
                                    >
                                      <MonetizationOnOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              <div
                style={{
                  marginTop: "12px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <div style={{ color: "#6B7280", fontSize: "12px" }}>
                  {`Total: ${totalObjects} | Página ${page + 1} de ${Math.max(totalPages, 1)}`}
                </div>

                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <TextField
                    select
                    SelectProps={{ native: true }}
                    label="Tamaño"
                    value={pageSize}
                    onChange={(e) => {
                      const nextSize = Number(e.target.value || 50);
                      setPage(0);
                      setPageSize(nextSize);
                    }}
                    style={{ width: "120px" }}
                  >
                    {[25, 50, 100].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </TextField>

                  <Button
                    className="btnSecundarioFlat"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={loading || page <= 0}
                  >
                    ANTERIOR
                  </Button>
                  <Button
                    className="btnSecundarioFlat"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={loading || totalPages === 0 || page + 1 >= totalPages}
                  >
                    SIGUIENTE
                  </Button>
                </div>
              </div>
            </Paper>
          </Col>
        </Row>
      )}

      <Dialog
        open={dialogState.open}
        onClose={cerrarDialogo}
        fullWidth
        maxWidth="sm"
        PaperProps={{ style: { fontFamily: poppinsFont } }}
      >
        <DialogTitle>
          {dialogState.type === "edit" && "Editar cuenta corriente"}
          {dialogState.type === "reset" && "Resetear saldo a límite"}
          {dialogState.type === "pago" && "Registrar pago parcial"}
        </DialogTitle>
        <DialogContent>
          {dialogState.type === "edit" && (
            <>
              <TextField
                margin="dense"
                fullWidth
                type="text"
                label="Límite"
                value={formatNumberForInput(dialogState.limite)}
                inputProps={{ inputMode: "decimal" }}
                onChange={(e) => {
                  const value = e.target.value;
                  updateDialogField("limite", parseFormattedNumberInput(value));
                }}
              />
              <TextField
                margin="dense"
                fullWidth
                type="text"
                label="Saldo disponible"
                value={formatNumberForInput(dialogState.saldoDisponible)}
                inputProps={{ inputMode: "decimal" }}
                onChange={(e) => {
                  const value = e.target.value;
                  updateDialogField("saldoDisponible", parseFormattedNumberInput(value));
                }}
              />
            </>
          )}

          {dialogState.type === "pago" && (
            <TextField
              margin="dense"
              fullWidth
              type="text"
              label="Monto de pago"
              value={formatNumberForInput(dialogState.montoPago)}
              inputProps={{ inputMode: "decimal" }}
              onChange={(e) => {
                const value = e.target.value;
                updateDialogField("montoPago", parseFormattedNumberInput(value));
              }}
            />
          )}

          <TextField
            margin="dense"
            fullWidth
            label="Motivo (obligatorio)"
            value={dialogState.motivo}
            onChange={(e) => {
              const value = e.target.value;
              updateDialogField("motivo", value);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarDialogo}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={confirmarDialogo}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CuentaCorriente;
